import type { TranslateFn } from 'src/i18n/shim/useTranslation';

/** A wire-style discriminant, not display copy: the walk runs in a plain module with no access to
 * `t`, and an `Error.message` may end up in `console.error` or RUM, which stay English. The render
 * site maps the code to a resource id instead. */
export type SnapshotFailureCode = 'page-limit' | 'no-data' | 'repeated-token';

/** A snapshot walk that stopped because one of its own integrity guards tripped, as opposed to a
 * network/GraphQL failure (which propagates unwrapped). Carries an English `message` for
 * diagnostics plus the structured `code` the UI keys its copy on — the same split
 * `DuplicateStringIdError` uses. */
export class SnapshotError extends Error {
  constructor(
    readonly code: SnapshotFailureCode,
    message: string,
  ) {
    super(message);
    // Restores the prototype chain across the ES5 downlevel target, without
    // which `instanceof SnapshotError` is false for a thrown instance.
    Object.setPrototypeOf(this, SnapshotError.prototype);
    this.name = 'SnapshotError';
  }
}

/** Explicit map, so every resource id is greppable as a literal. */
const SNAPSHOT_FAILURE_RESOURCE_IDS: Record<SnapshotFailureCode, string> = {
  'page-limit': 'sgd-snapshot-page-limit',
  'no-data': 'sgd-snapshot-no-data',
  'repeated-token': 'sgd-snapshot-repeated-token',
};

/** The `kind` tag is what the caller needs: our own guard copy is already a complete localized
 * sentence, so wrapping it in a "Failed to load X: {detail}" frame would nest two sentences saying
 * the same thing. Upstream wording is the opposite — content, untranslated, and only correct
 * inside a localized frame. */
export type LoadFailureText =
  | { readonly kind: 'own'; readonly text: string }
  | { readonly kind: 'upstream'; readonly text: string }
  | { readonly kind: 'none' };

/** Shared by all four snapshot readers (strings table, view-version, diff, rollback) so one
 * throwable is described identically wherever it surfaces. */
export function describeLoadFailure(
  t: TranslateFn,
  thrown: unknown,
): LoadFailureText {
  if (thrown instanceof SnapshotError) {
    return { kind: 'own', text: t(SNAPSHOT_FAILURE_RESOURCE_IDS[thrown.code]) };
  }
  // An empty message is treated as no message: it would render the frame with a
  // dangling colon ("Failed to load strings: ").
  if (thrown instanceof Error && thrown.message) {
    return { kind: 'upstream', text: thrown.message };
  }
  return { kind: 'none' };
}
